import React, { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CHROME_IMAGES } from "@/data/mockData";
import { useAgents } from "@/data/agentsStore";
import { inboxApi } from "@/lib/api";
import { PageHeader, OfferBadge, Pill, EditorialButton } from "@/lib/editorial";
import { Search, ExternalLink, RefreshCw, Send, Sparkles, X, ChevronDown, ChevronUp } from "lucide-react";
import { fmtCET } from "@/lib/utils";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TABS = ["All", "Unread"];

export default function Inbox() {
  const qc = useQueryClient();
  const agents = useAgents();
  const threadEndRef = useRef(null);

  const [search, setSearch] = useState("");
  const [agentFilter, setAgentFilter] = useState("all");
  const [tab, setTab] = useState("All");
  // activeChat: { chatId, agentId, name, linkedinUrl }
  const [activeChat, setActiveChat] = useState(null);
  const [reply, setReply] = useState("");
  const [suggestion, setSuggestion] = useState(null); // { text, isRedirect }
  const [headerVisible, setHeaderVisible] = useState(true);

  const { data: conversations = [], refetch, isFetching } = useQuery({
    queryKey: ["inbox", agentFilter, search],
    queryFn: () => inboxApi.list({ agentId: agentFilter === "all" ? "" : agentFilter, search }),
    refetchInterval: 30_000,
  });

  const { data: thread, isLoading: threadLoading } = useQuery({
    queryKey: ["thread-chat", activeChat?.chatId, activeChat?.agentId],
    queryFn: () => inboxApi.threadByChat(activeChat.chatId, activeChat.agentId, activeChat.attendeeProviderId),
    enabled: !!(activeChat?.chatId),
    refetchInterval: 10_000,
  });

  // When thread loads with a resolved person name, update the activeChat display name
  useEffect(() => {
    if (thread?.personName && activeChat && !activeChat.nameResolved) {
      setActiveChat(prev => prev ? {
        ...prev,
        name: thread.personName,
        linkedinUrl: thread.personLinkedinUrl || prev.linkedinUrl,
        nameResolved: true,
      } : prev);
    }
  }, [thread?.personName]);

  // Scroll to bottom when thread loads/updates
  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [thread?.messages?.length]);

  const suggestMut = useMutation({
    mutationFn: () => inboxApi.suggestReply(activeChat.chatId, activeChat.agentId, activeChat.attendeeProviderId),
    onSuccess: (data) => setReply(data.suggestion),
    onError: (e) => toast.error(e.message || "Could not generate suggestion."),
  });

  const replyMut = useMutation({
    mutationFn: (text) => inboxApi.replyByChat(activeChat.chatId, activeChat.agentId, text),
    onSuccess: () => {
      setReply("");
      toast("Message sent.");
      qc.invalidateQueries({ queryKey: ["thread-chat", activeChat?.chatId] });
      qc.invalidateQueries({ queryKey: ["inbox"] });
    },
    onError: (e) => toast.error(e.message || "Send failed."),
  });

  const filtered = conversations.filter((c) => {
    if (tab === "Unread" && !c.unread) return false;
    return true;
  });

  const activeMessages = thread?.messages ?? [];
  const activeAgentName = activeChat
    ? (agents.find((a) => a.id === activeChat.agentId)?.name ?? thread?.agentName ?? "")
    : "";

  return (
    <div className="text-[var(--ink)] h-full flex flex-col">
      {headerVisible ? (
        <div className="relative bg-[var(--ink)] text-white overflow-hidden">
          <img src={CHROME_IMAGES.headerDecor} alt="" className="chrome-img absolute inset-0 w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-[var(--ink)] via-[var(--ink)]/85 to-transparent" />
          <div className="relative px-8 lg:px-14 py-6">
            <PageHeader
              eyebrow="Inbox"
              tape="Every"
              rest="conversation"
              dark
              right={
                <div className="flex items-center gap-2">
                  <EditorialButton variant="dark" onClick={() => refetch()} data-testid="inbox-sync">
                    <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? "animate-spin" : ""}`} /> Refresh
                  </EditorialButton>
                  <button onClick={() => setHeaderVisible(false)} className="border border-white/30 text-white/70 hover:text-white hover:border-white/60 p-2 transition-colors" title="Collapse header">
                    <ChevronUp className="w-3.5 h-3.5" />
                  </button>
                </div>
              }
              testId="inbox-title"
            />
          </div>
        </div>
      ) : (
        <div className="bg-[var(--ink)] text-white px-8 lg:px-14 py-2 flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-[0.22em] text-white/60">Inbox</span>
          <div className="flex items-center gap-2">
            <button onClick={() => refetch()} className="text-white/60 hover:text-white p-1 transition-colors" title="Refresh">
              <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? "animate-spin" : ""}`} />
            </button>
            <button onClick={() => setHeaderVisible(true)} className="border border-white/30 text-white/70 hover:text-white hover:border-white/60 p-2 transition-colors" title="Expand header">
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[420px_1fr] min-h-0">
        {/* Left column */}
        <div className="border-r border-[var(--rule-light)] flex flex-col min-h-0">
          <div className="p-4 border-b border-[var(--rule-light)] space-y-3">
            <div className="flex items-center gap-2 border border-[var(--rule-light)] px-3">
              <Search className="w-3.5 h-3.5 text-[var(--muted-light)]" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name"
                className="flex-1 bg-transparent outline-none py-2 text-[13px]"
                data-testid="inbox-search"
              />
            </div>
            <Select value={agentFilter} onValueChange={setAgentFilter}>
              <SelectTrigger className="rounded-none border-[var(--rule-light)] focus:ring-0 h-9 text-[12px] uppercase tracking-[0.16em]" data-testid="inbox-agent-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All agents</SelectItem>
                {agents.map((a) => (
                  <SelectItem key={a.id} value={String(a.id)}>{a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex gap-1">
              {TABS.map((t) => (
                <button key={t} onClick={() => setTab(t)}
                  className={`text-[10px] uppercase tracking-[0.18em] px-2 py-1 border ${tab === t ? "bg-[var(--ink)] border-[var(--ink)] text-white" : "border-[var(--rule-light)]"}`}>
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto" data-testid="inbox-list">
            {isFetching && filtered.length === 0 && (
              <div className="text-center py-14 text-[var(--muted-light)] text-[13px]">Loading conversations…</div>
            )}
            {!isFetching && filtered.length === 0 && (
              <div className="text-center py-14 text-[var(--muted-light)] italic font-display">No conversations.</div>
            )}
            {filtered.map((c) => {
              const isActive = activeChat?.chatId === c.chatId;
              return (
                <button
                  key={c.chatId}
                  onClick={() => setActiveChat({ chatId: c.chatId, agentId: c.agentId, name: c.name || "…", linkedinUrl: c.linkedinUrl, attendeeProviderId: c.attendeeProviderId, nameResolved: !!c.name })}
                  className={`w-full text-left px-4 py-3 border-b border-[var(--rule-light)] flex items-start gap-3 transition-colors ${isActive ? "bg-[var(--paper-2)]" : "hover:bg-[var(--paper-2)]"}`}>
                  <span className={`mt-2 w-2 h-2 rounded-full shrink-0 ${c.unread ? "bg-[var(--blue)]" : "bg-transparent border border-gray-300"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="font-display text-[15px] truncate">{c.name || <span className="text-[var(--muted-light)] italic">Loading…</span>}</div>
                      <span className="text-[10px] font-mono text-[var(--muted-light)] ml-auto shrink-0">{relativeTime(c.lastMessageAt)}</span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      <Pill color="dark" className="!bg-[#0a0a0a] !text-white !border-[#0a0a0a]">{c.agentName?.split(" ")[0]}</Pill>
                      {c.offerId && <OfferBadge offerId={c.offerId} />}
                    </div>
                    <div className="text-[12px] text-[var(--muted-light)] mt-1.5 truncate">{c.lastSnippet}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Right column — thread */}
        {activeChat ? (
          <div className="flex flex-col min-h-0">
            {/* Header */}
            <div className="px-6 py-3 border-b border-[var(--rule-light)] flex items-center gap-4 flex-wrap">
              <div>
                <div className="font-display text-[20px] leading-tight">{activeChat.name}</div>
                {activeChat.linkedinUrl && (
                  <a href={activeChat.linkedinUrl} target="_blank" rel="noopener noreferrer"
                    className="text-[12px] text-[var(--blue)] hover:underline flex items-center gap-1 mt-0.5">
                    View on LinkedIn <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
              <div className="ml-auto">
                <Pill color="dark">{activeAgentName}</Pill>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-4 bg-[var(--paper-2)]" data-testid="inbox-thread">
              {threadLoading && (
                <div className="text-center text-[var(--muted-light)] text-[13px]">Loading messages…</div>
              )}
              {activeMessages.map((m, i) => {
                const displayName = m.outbound
                  ? (activeAgentName || "You")
                  : (m.senderName || activeChat.name || "Prospect");
                return (
                  <div key={m.id || i} className={`flex ${m.outbound ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[72%] flex flex-col ${m.outbound ? "items-end" : "items-start"}`}>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--muted-light)] mb-1">
                        {displayName}
                        {m.ts ? <span className="ml-1 normal-case tracking-normal">· {fmtCET(m.ts)}</span> : ""}
                      </div>
                      <div className={`px-4 py-3 text-[14px] leading-relaxed whitespace-pre-wrap rounded-sm ${
                        m.outbound
                          ? "bg-[var(--ink)] text-white"
                          : "bg-white border border-[var(--rule-light)] text-[var(--ink)]"
                      }`}>
                        {m.text}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={threadEndRef} />
            </div>

            {/* Reply box */}
            <div className="border-t border-[var(--rule-light)] bg-white" data-testid="inbox-reply-area">
              <div className="p-4">
              <textarea rows={3} value={reply} onChange={(e) => setReply(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && reply.trim()) replyMut.mutate(reply); }}
                placeholder="Type a reply… (Cmd+Enter to send)"
                className="w-full border border-[var(--rule-light)] focus:border-[var(--blue)] outline-none p-3 text-[14px]"
                data-testid="inbox-reply-textarea" />
              <div className="flex items-center justify-between mt-2">
                <span className="text-[11px] font-mono text-[var(--muted-light)]">{reply.length} / 2000</span>
                <div className="flex items-center gap-2">
                  <button onClick={() => suggestMut.mutate()} disabled={suggestMut.isPending}
                    className="border border-[var(--rule-light)] px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] flex items-center gap-2 hover:bg-[var(--paper-2)] disabled:opacity-30 disabled:cursor-not-allowed">
                    <Sparkles className="w-3.5 h-3.5" /> {suggestMut.isPending ? "Thinking…" : "Suggest reply"}
                  </button>
                  <button onClick={() => replyMut.mutate(reply)} disabled={!reply.trim() || replyMut.isPending}
                    data-testid="inbox-send"
                    className="bg-[var(--blue)] hover:bg-[var(--blue-hover)] disabled:opacity-30 disabled:cursor-not-allowed text-white px-5 py-2.5 text-[11px] uppercase tracking-[0.22em] flex items-center gap-2">
                    <Send className="w-3.5 h-3.5" /> {replyMut.isPending ? "Sending…" : "Send"}
                  </button>
                </div>
              </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center text-center p-12 bg-[var(--paper-2)] relative overflow-hidden">
            <img src={CHROME_IMAGES.emptyState} alt="" className="chrome-img absolute inset-0 w-full h-full object-cover opacity-25" />
            <div className="relative">
              <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--muted-light)] mb-2">Inbox idle</div>
              <div className="font-display italic text-3xl">Select a conversation.</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function relativeTime(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  if (isNaN(diff)) return "";
  const h = Math.floor(diff / (1000 * 60 * 60));
  if (h < 1) return "Just now";
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return d === 1 ? "Yesterday" : `${d}d ago`;
}
